import { useState, useCallback } from 'react';

import { useAuth } from '@/shared/contexts/AuthContext';
import {
    likePost,
    dislikePost,
    bookmarkPost,
    removeBookmark,
    checkEngagement,
} from '@/shared/services/engagementApi';

/**
 * Manages post engagement state (likes, dislikes, bookmarks) for a list of posts.
 *
 * Auth guard: handlers return early silently when no user is present.
 * Flash messages for unauthenticated users are the calling page's responsibility.
 *
 * @param {object} axiosInstance - Authenticated axios instance from the calling page
 * @param {object} options
 * @param {Function} options.onReaction - Callback invoked after a like/dislike with
 *   (postId, { likeCount, dislikeCount }); caller decides how to apply the updated counts
 * @param {Function} options.onError - Callback invoked after an error occurs
 */
export default function usePostEngagement(
    axiosInstance,
    { onReaction, onError }
) {
    const { user } = useAuth();
    const [engagements, setEngagements] = useState({});

    const fetchEngagements = useCallback(
        async (postList) => {
            if (!user || !axiosInstance || postList.length === 0) return;

            try {
                const results = await Promise.all(
                    postList.map((post) =>
                        checkEngagement(axiosInstance, post._id)
                            .then((data) => [post._id, data])
                            .catch(() => [post._id, {}])
                    )
                );
                setEngagements(Object.fromEntries(results));
            } catch (err) {
                console.error('Error checking engagements:', err);
            }
        },
        [user, axiosInstance]
    );

    const handleLike = useCallback(
        async (postId) => {
            if (!user || !axiosInstance) return;

            try {
                const data = await likePost(axiosInstance, postId);
                const { reaction, likeCount, dislikeCount } = data;

                setEngagements((prev) => ({
                    ...prev,
                    [postId]: { ...prev[postId], reaction },
                }));

                onReaction(postId, { likeCount, dislikeCount });
            } catch (err) {
                console.error('Error updating like:', err);
                if (err.response?.status === 403) {
                    onError(
                        err.response?.data?.message ||
                            'Unable to react to this post.',
                        postId
                    );
                }
            }
        },
        [user, axiosInstance, onReaction, onError]
    );

    const handleDislike = useCallback(
        async (postId) => {
            if (!user || !axiosInstance) return;

            try {
                const data = await dislikePost(axiosInstance, postId);
                const { reaction, likeCount, dislikeCount } = data;

                setEngagements((prev) => ({
                    ...prev,
                    [postId]: { ...prev[postId], reaction },
                }));

                onReaction(postId, { likeCount, dislikeCount });
            } catch (err) {
                console.error('Error updating dislike:', err);
                if (err.response?.status === 403) {
                    onError(
                        err.response?.data?.message ||
                            'Unable to react to this post.',
                        postId
                    );
                }
            }
        },
        [user, axiosInstance, onReaction, onError]
    );

    const handleBookmark = useCallback(
        async (postId) => {
            if (!user || !axiosInstance) return;

            const isBookmarked = engagements[postId]?.isBookmarked;

            setEngagements((prev) => ({
                ...prev,
                [postId]: { ...prev[postId], isBookmarked: !isBookmarked },
            }));

            try {
                if (isBookmarked) {
                    await removeBookmark(axiosInstance, postId);
                } else {
                    await bookmarkPost(axiosInstance, postId);
                }
            } catch (err) {
                setEngagements((prev) => ({
                    ...prev,
                    [postId]: { ...prev[postId], isBookmarked },
                }));
                console.error('Error updating bookmark:', err);
            }
        },
        [user, axiosInstance, engagements]
    );

    return {
        engagements,
        fetchEngagements,
        handleLike,
        handleDislike,
        handleBookmark,
    };
}
